import React, { useEffect, useRef, useState } from 'react';
import { Gift, getGifts } from './api';

interface Props {
  onSelect: (giftName: string, tiktokGiftId?: number | null) => void;
  onClose: () => void;
}

const PAGE_SIZE = 24;
const isMobile = window.innerWidth < 768;

// Déclencheur spécial « nouvel abonné » (doit matcher FOLLOW_TRIGGER backend).
export const FOLLOW_TRIGGER = '__follow__';

const giftImageUrl = (url: string) =>
  `/api/gift-image?url=${encodeURIComponent(url)}`;

function FollowPickCard({ onClick, mobile }: { onClick: () => void; mobile?: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Déclencher l'animation à chaque nouvel abonné"
      style={{
        cursor: 'pointer', borderRadius: 10, overflow: 'hidden',
        border: `1px solid ${hover ? '#16a34a' : '#14532d'}`,
        background: 'linear-gradient(160deg,#0f2a1a,#0a1f13)', transition: 'all 0.15s',
        transform: hover ? 'scale(1.04)' : 'scale(1)',
      }}
    >
      <div style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: mobile ? 26 : 30 }}>
        🔔
      </div>
      <div style={{ padding: mobile ? '5px 4px' : '4px 5px' }}>
        <span style={{ color: hover ? '#86efac' : '#4ade80', fontSize: mobile ? 10 : 9, fontWeight: 700, display: 'block', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Nouvel abonné
        </span>
      </div>
    </div>
  );
}

export default function GiftPicker({ onSelect, onClose }: Props) {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tiktokOnly, setTiktokOnly] = useState(true);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getGifts().then(r => setGifts(r.data)).catch(() => {});
    if (!isMobile) setTimeout(() => searchRef.current?.focus(), 50);
  }, []);

  const tiktokGifts = tiktokOnly ? gifts.filter(g => !!g.image_url) : gifts;

  const filtered = search.trim()
    ? tiktokGifts.filter(g => {
        const q = search.toLowerCase();
        return (g.name_fr || g.name).toLowerCase().includes(q) || g.name.toLowerCase().includes(q);
      })
    : tiktokGifts;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const exactMatch = filtered.some(g =>
    (g.name_fr || g.name).toLowerCase() === search.trim().toLowerCase() ||
    g.name.toLowerCase() === search.trim().toLowerCase()
  );

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const pick = (name: string, tiktokGiftId?: number | null) => { onSelect(name, tiktokGiftId); onClose(); };

  if (isMobile) {
    return (
      <div
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      >
        <div style={{ background: '#161616', borderRadius: '16px 16px 0 0', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#333' }} />
          </div>

          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 16px 10px' }}>
            <span style={{ color: '#a78bfa', fontWeight: 700, fontSize: 15 }}>🎁 Choisir un cadeau</span>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>

          {/* Search + TikTok toggle */}
          <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px' }}>
            <input
              ref={searchRef}
              placeholder="Rechercher un cadeau…"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && search.trim()) {
                  const match = filtered[0];
                  pick(match ? (match.name_fr || match.name) : search.trim(), match?.tiktok_gift_id);
                }
              }}
              style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #2e2e2e', background: '#111', color: '#fff', fontSize: 14, outline: 'none' }}
            />
            <button
              onClick={() => { setTiktokOnly(t => !t); setPage(1); }}
              style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${tiktokOnly ? '#7c3aed' : '#333'}`, background: tiktokOnly ? '#2d1a4e' : '#111', color: tiktokOnly ? '#c4b5fd' : '#666', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {tiktokOnly ? '✓ TikTok' : 'Tous'}
            </button>
          </div>

          {/* Grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, alignContent: 'start' }}>
            {page === 1 && !search.trim() && (
              <FollowPickCard onClick={() => pick(FOLLOW_TRIGGER, null)} mobile />
            )}
            {paginated.map(gift => (
              <GiftCard key={gift.id} gift={gift} onClick={() => pick(gift.name_fr || gift.name, gift.tiktok_gift_id)} mobile />
            ))}

            {search.trim() && !exactMatch && (
              <div
                onClick={() => pick(search.trim(), undefined)}
                style={{ cursor: 'pointer', borderRadius: 10, border: '1px dashed #444', background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 6px', gap: 5, minHeight: 80 }}
              >
                <span style={{ color: '#7c3aed', fontSize: 20 }}>+</span>
                <span style={{ color: '#888', fontSize: 9, textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.3 }}>"{search.trim()}"</span>
              </div>
            )}

            {filtered.length === 0 && !search && (
              <div style={{ gridColumn: '1 / -1', color: '#444', fontSize: 13, textAlign: 'center', padding: '24px 0', lineHeight: 1.8 }}>
                {tiktokOnly ? (
                  <><span>Aucun cadeau TikTok.</span><br />
                  <span style={{ color: '#555', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setTiktokOnly(false)}>Afficher tous</span></>
                ) : (
                  <span>Aucun cadeau dans la bibliothèque.</span>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ borderTop: '1px solid #1e1e1e', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #2a2a2a', background: '#111', color: page === 1 ? '#333' : '#888', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}
              >← Préc.</button>
              <span style={{ color: '#555', fontSize: 12 }}>{page} / {totalPages} · {filtered.length}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #2a2a2a', background: '#111', color: page === totalPages ? '#333' : '#888', cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}
              >Suiv. →</button>
            </div>
          )}
          {totalPages <= 1 && (
            <div style={{ height: 'env(safe-area-inset-bottom, 8px)', flexShrink: 0 }} />
          )}
        </div>
      </div>
    );
  }

  // ── Desktop ───────────────────────────────────────────────
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div style={{ background: '#161616', border: '1px solid #2a2a2a', borderRadius: 14, width: '100%', maxWidth: 500, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>

        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ color: '#a78bfa', fontWeight: 700, fontSize: 13 }}>🎁 Choisir un cadeau</span>
          <input
            ref={searchRef}
            placeholder="Rechercher un cadeau…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && search.trim()) {
                const match = filtered[0];
                pick(match ? (match.name_fr || match.name) : search.trim(), match?.tiktok_gift_id);
              }
              if (e.key === 'Escape') onClose();
            }}
            style={{ flex: 1, padding: '7px 11px', borderRadius: 7, border: '1px solid #2e2e2e', background: '#111', color: '#fff', fontSize: 12 }}
          />
          <button
            onClick={() => { setTiktokOnly(t => !t); setPage(1); }}
            style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${tiktokOnly ? '#7c3aed' : '#333'}`, background: tiktokOnly ? '#2d1a4e' : '#111', color: tiktokOnly ? '#c4b5fd' : '#666', fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {tiktokOnly ? '✓ TikTok' : 'Tous'}
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 20, lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>

        {/* Grille */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8, alignContent: 'start' }}>
          {page === 1 && !search.trim() && (
            <FollowPickCard onClick={() => pick(FOLLOW_TRIGGER, null)} />
          )}
          {paginated.map(gift => (
            <GiftCard key={gift.id} gift={gift} onClick={() => pick(gift.name_fr || gift.name, gift.tiktok_gift_id)} />
          ))}

          {search.trim() && !exactMatch && (
            <div
              onClick={() => pick(search.trim(), undefined)}
              style={{ cursor: 'pointer', borderRadius: 10, border: '1px dashed #444', background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 6px', gap: 5, minHeight: 90 }}
            >
              <span style={{ color: '#7c3aed', fontSize: 22 }}>+</span>
              <span style={{ color: '#888', fontSize: 10, textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.3 }}>"{search.trim()}"</span>
            </div>
          )}

          {filtered.length === 0 && !search && (
            <div style={{ gridColumn: '1 / -1', color: '#444', fontSize: 12, textAlign: 'center', padding: '24px 0', lineHeight: 1.8 }}>
              {tiktokOnly ? (
                <>Aucun cadeau TikTok dans la bibliothèque.<br />
                <span style={{ color: '#555', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setTiktokOnly(false)}>Afficher tous les cadeaux</span></>
              ) : (
                <>Aucun cadeau dans la bibliothèque.<br />
                <span style={{ color: '#333' }}>Ils s'ajoutent automatiquement lors des lives TikTok.</span><br />
                <span style={{ color: '#555' }}>Tu peux aussi taper un nom ci-dessus.</span></>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ borderTop: '1px solid #1e1e1e', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #2a2a2a', background: '#111', color: page === 1 ? '#333' : '#888', cursor: page === 1 ? 'default' : 'pointer', fontSize: 12 }}
            >← Préc.</button>
            <span style={{ color: '#444', fontSize: 11 }}>{page} / {totalPages} &nbsp;·&nbsp; {filtered.length} cadeaux</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #2a2a2a', background: '#111', color: page === totalPages ? '#333' : '#888', cursor: page === totalPages ? 'default' : 'pointer', fontSize: 12 }}
            >Suiv. →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function GiftCard({ gift, onClick, mobile }: { gift: Gift; onClick: () => void; mobile?: boolean }) {
  const [hover, setHover] = useState(false);
  const [imgError, setImgError] = useState(false);
  const hue = gift.name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: 'pointer', borderRadius: mobile ? 10 : 10, overflow: 'hidden',
        border: `1px solid ${hover ? '#7c3aed' : '#1e1e1e'}`,
        background: '#111', transition: 'all 0.15s',
        transform: hover ? 'scale(1.04)' : 'scale(1)',
      }}
    >
      {gift.image_url && !imgError ? (
        <img
          src={giftImageUrl(gift.image_url)}
          alt=""
          loading="lazy"
          onError={() => setImgError(true)}
          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{ width: '100%', aspectRatio: '1', background: `hsl(${hue},40%,15%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: mobile ? 26 : 30 }}>
          🎁
        </div>
      )}
      <div style={{ padding: mobile ? '5px 4px' : '4px 5px', background: hover ? '#1a1030' : 'transparent', transition: 'background 0.15s' }}>
        <span style={{ color: hover ? '#c4b5fd' : '#aaa', fontSize: mobile ? 10 : 9, fontWeight: 600, display: 'block', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {gift.name_fr || gift.name}
        </span>
      </div>
    </div>
  );
}
